import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { reportClientError } from '../lib/install-error-reporter';
import { Button } from './ui/button';

// Top-level error boundary for every route. Handles two categories:
//
//  1. "Failed to fetch dynamically imported module" — the open tab
//     references a chunk hash that no longer exists because a new
//     deploy rotated them. Auto-reload once (with a guard cookie so
//     we don't loop) and the new HTML pulls the fresh hashes.
//  2. Anything else — show a readable fallback panel with a reload
//     button instead of React Router's "Hey developer" placeholder.

const CHUNK_ERROR_SIGNATURE = 'Failed to fetch dynamically imported module';
const RELOAD_GUARD_KEY = 'food-diary.chunk-reload-once';

function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unknown error';
}

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error && error.message.includes(CHUNK_ERROR_SIGNATURE)) return true;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const m = (error as { readonly message: unknown }).message;
    if (typeof m === 'string' && m.includes(CHUNK_ERROR_SIGNATURE)) return true;
  }
  return false;
}

export function RouterErrorBoundary(): React.JSX.Element {
  const error = useRouteError();
  const [looped, setLooped] = useState(false);

  useEffect(() => {
    if (!isChunkLoadError(error)) return;
    // Reload exactly once — if the fresh HTML still can't load its
    // chunks, we're on a broken deploy and the user deserves a real
    // error panel instead of an infinite reload.
    const already = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (already) {
      setLooped(true);
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    globalThis.location.reload();
  }, [error]);

  // Forward render-time errors to the client-error beacon so we can
  // see what blew up. Skip chunk-load errors — those are recovered by
  // the reload above and would just generate noise.
  useEffect(() => {
    if (isChunkLoadError(error)) return;
    const status = isRouteErrorResponse(error) ? error.status : undefined;
    reportClientError({
      kind: 'react',
      message: formatErrorMessage(error),
      stack: error instanceof Error ? error.stack : undefined,
      ...(status === undefined ? {} : { httpStatus: status }),
    });
  }, [error]);

  const chunk = isChunkLoadError(error);
  const status = isRouteErrorResponse(error) ? error.status : null;
  const message = formatErrorMessage(error);

  return (
    <div className="mx-auto max-w-xl space-y-4 p-8">
      <h1 className="heading text-3xl text-[var(--color-ink)]">Something went wrong</h1>
      {chunk && !looped ? (
        <p className="text-[var(--color-ink-muted)]">Loading a fresh build — one moment…</p>
      ) : (
        <>
          <p className="text-[var(--color-ink-muted)]">
            {chunk
              ? 'Reload did not recover. A new deploy may still be rolling out; try again in a minute.'
              : 'The app hit an unexpected error. Reloading usually clears it.'}
          </p>
          {status === null ? null : (
            <p className="num-data text-sm text-[var(--color-ink-subtle)]">Status {status}</p>
          )}
          <pre className="num-data overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-xs text-[var(--color-ink)]">
            {message}
          </pre>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                sessionStorage.removeItem(RELOAD_GUARD_KEY);
                globalThis.location.reload();
              }}
            >
              Reload
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                globalThis.location.assign('/');
              }}
            >
              Go home
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
